import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { Avatar, CardTitle, Icon, Spinner, Switch } from '../components/ui';
import { ACCENT_SWATCHES, applyAccent, getAccent, type Accent } from '../lib/accent';

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  last_used_at: string | null;
  created_at: string;
  is_active: boolean;
}

const TIER_RESTRICTED_CODES = ['AUTH_FORBIDDEN', 'USAGE_LIMIT_EXCEEDED'];

const PREFS_KEY = 'audiaro_prefs';

interface Prefs {
  autoplay: boolean;
  hd: boolean;
  emailNotif: boolean;
}

const DEFAULT_PREFS: Prefs = { autoplay: true, hd: false, emailNotif: true };

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    // Corrupt storage — fall back to defaults.
  }
  return DEFAULT_PREFS;
}

const PREF_ROWS: { key: keyof Prefs; title: string; desc: string }[] = [
  { key: 'autoplay', title: 'Autoplay previews', desc: 'Play voice samples on hover' },
  { key: 'hd', title: 'HD audio by default', desc: '48kHz export · Pro feature' },
  { key: 'emailNotif', title: 'Email notifications', desc: 'Notify when generation completes' },
];

export function Settings() {
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  // Preferences (localStorage-backed)
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);

  // Accent theme
  const [accent, setAccent] = useState<Accent>(getAccent);

  // API keys state
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [showNewKey, setShowNewKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [createdKey, setCreatedKey] = useState<{ name: string; key: string } | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [revokeConfirm, setRevokeConfirm] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [tierRestricted, setTierRestricted] = useState(false);

  const isStarter = user?.tier.slug === 'starter';
  const apiAccessBlocked = isStarter || tierRestricted;

  const initials =
    ((user?.first_name?.[0] || '') + (user?.last_name?.[0] || '')).toUpperCase() ||
    (user?.email?.[0] || '?').toUpperCase();

  // Keep profile form in sync when the user loads/refreshes
  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email,
      });
    }
  }, [user]);

  // Load API keys (single-page layout: load on mount when the tier allows it)
  useEffect(() => {
    if (!isStarter) {
      loadApiKeys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStarter]);

  const loadApiKeys = async () => {
    setKeysLoading(true);
    const result = await api.getApiKeys();
    if (result.success && result.data) {
      setApiKeys(result.data);
      setTierRestricted(false);
    } else if (result.error && TIER_RESTRICTED_CODES.includes(result.error.code)) {
      setTierRestricted(true);
    } else if (result.error) {
      setMessage({ type: 'error', text: result.error.message });
    }
    setKeysLoading(false);
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    const result = await api.updateProfile({
      first_name: profileData.first_name.trim(),
      last_name: profileData.last_name.trim(),
    });

    if (result.success) {
      await refreshUser();
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } else {
      setMessage({ type: 'error', text: result.error?.message || 'Failed to update profile' });
    }
    setSaving(false);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setSaving(false);
      return;
    }

    const result = await api.changePassword({
      current_password: passwordData.current_password,
      new_password: passwordData.new_password,
    });

    if (result.success) {
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } else {
      setMessage({ type: 'error', text: result.error?.message || 'Failed to change password' });
    }
    setSaving(false);
  };

  const togglePref = (key: keyof Prefs) => {
    setPrefs((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(PREFS_KEY, JSON.stringify(next));
      } catch {
        // Storage unavailable — keep in-memory state only.
      }
      return next;
    });
  };

  const handleAccent = (key: Accent) => {
    applyAccent(key);
    setAccent(key);
  };

  const handleCreateKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    setCreatingKey(true);
    setMessage(null);

    const result = await api.createApiKey(newKeyName.trim());

    if (result.success && result.data) {
      setCreatedKey({ name: result.data.name, key: result.data.key });
      setKeyCopied(false);
      setNewKeyName('');
      setShowNewKey(false);
      setApiKeys([
        {
          id: result.data.id,
          name: result.data.name,
          key_prefix: result.data.key_prefix,
          last_used_at: null,
          created_at: result.data.created_at,
          is_active: result.data.is_active,
        },
        ...apiKeys,
      ]);
    } else if (result.error && TIER_RESTRICTED_CODES.includes(result.error.code)) {
      setTierRestricted(true);
    } else {
      setMessage({ type: 'error', text: result.error?.message || 'Failed to create API key' });
    }
    setCreatingKey(false);
  };

  const handleCopyKey = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } catch {
      setMessage({ type: 'error', text: 'Failed to copy to clipboard' });
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    setRevoking(true);
    const result = await api.deleteApiKey(keyId);
    if (result.success) {
      // The backend soft-deletes (is_active = 0) and GET still returns the
      // key, so keep it in state as inactive to match what a reload shows.
      setApiKeys(apiKeys.map((k) => (k.id === keyId ? { ...k, is_active: false } : k)));
    } else {
      setMessage({ type: 'error', text: result.error?.message || 'Failed to revoke API key' });
    }
    setRevokeConfirm(null);
    setRevoking(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="fx max-w-[760px] flex flex-col gap-[18px]">
      {/* Feedback */}
      {message && (
        <div
          className={`rounded-[12px] border px-4 py-3 text-[13px] font-medium ${
            message.type === 'success'
              ? 'border-[#2E8B57]/50 bg-[#2E8B57]/10 text-[#7BD0A0]'
              : 'border-[#D8362F]/50 bg-[#D8362F]/10 text-[#F0928C]'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Profile */}
      <div className="card">
        <div className="mb-[18px]">
          <CardTitle>Profile</CardTitle>
        </div>
        <form onSubmit={handleProfileSubmit}>
          <div className="flex items-center gap-4 mb-5">
            <Avatar text={initials} size={60} className="border-2 border-[color:var(--a-lo)]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px]">
            <div>
              <label className="label">First name</label>
              <input
                type="text"
                value={profileData.first_name}
                onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="label">Last name</label>
              <input
                type="text"
                value={profileData.last_name}
                onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                className="input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Email</label>
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                className="input disabled:opacity-60"
                disabled
              />
              <p className="mt-[7px] text-xs text-mist-500">
                Contact support to change your email address.
              </p>
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <button type="submit" disabled={saving} className="btn-grad">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Preferences */}
      <div className="card">
        <div className="mb-2">
          <CardTitle>Preferences</CardTitle>
        </div>
        {PREF_ROWS.map((row) => (
          <div
            key={row.key}
            className="flex items-center justify-between gap-4 py-[14px] border-t border-ink-800 cursor-pointer"
            onClick={() => togglePref(row.key)}
          >
            <div className="min-w-0">
              <p className="m-0 text-mist-200 text-sm font-semibold">{row.title}</p>
              <p className="mt-[2px] m-0 text-mist-500 text-[12.5px]">{row.desc}</p>
            </div>
            <Switch checked={prefs[row.key]} onChange={() => togglePref(row.key)} />
          </div>
        ))}
      </div>

      {/* Theme */}
      <div className="card">
        <div className="mb-4">
          <CardTitle>Theme</CardTitle>
        </div>
        <div className="flex flex-wrap gap-5">
          {(Object.keys(ACCENT_SWATCHES) as Accent[]).map((key) => {
            const [hi, lo] = ACCENT_SWATCHES[key];
            const active = accent === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleAccent(key)}
                className="flex flex-col items-center gap-2 bg-transparent border-0 cursor-pointer"
                aria-pressed={active}
              >
                <span
                  className="block rounded-full"
                  style={{
                    width: 34,
                    height: 34,
                    background: `linear-gradient(135deg, ${hi}, ${lo})`,
                    boxShadow: active ? '0 0 0 3px #17171B, 0 0 0 5px var(--a-core)' : 'none',
                  }}
                />
                <span
                  className={`text-[11px] capitalize font-semibold ${
                    active ? 'text-mist-200' : 'text-mist-500'
                  }`}
                >
                  {key}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* API keys */}
      <div className="card">
        <div className="flex items-center justify-between gap-3 mb-4">
          <CardTitle>API keys</CardTitle>
          {!apiAccessBlocked && (
            <button
              type="button"
              className="btn-accent-outline"
              onClick={() => setShowNewKey((v) => !v)}
            >
              New key
            </button>
          )}
        </div>

        {apiAccessBlocked ? (
          <div
            className="rounded-[12px] border p-4"
            style={{ borderColor: 'rgba(var(--a-rgb), 0.35)', background: 'rgba(var(--a-rgb), 0.06)' }}
          >
            <h3 className="m-0 text-sm font-semibold" style={{ color: 'var(--a-hi)' }}>
              API access not available
            </h3>
            <p className="mt-1 mb-0 text-[13px] text-mist-400">
              Upgrade to Pro or Enterprise to access the API.
            </p>
            <a
              href="#billing"
              className="mt-2 inline-block text-[13px] font-semibold underline"
              style={{ color: 'var(--a-hi)' }}
            >
              View plans
            </a>
          </div>
        ) : (
          <div>
            {/* One-time full key display */}
            {createdKey && (
              <div
                className="rounded-[12px] border p-4 mb-4"
                style={{
                  borderColor: 'rgba(var(--a-rgb), 0.45)',
                  background: 'rgba(var(--a-rgb), 0.07)',
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="m-0 text-sm font-semibold" style={{ color: 'var(--a-hi)' }}>
                      API key "{createdKey.name}" created
                    </h3>
                    <p className="mt-1 mb-0 text-[12.5px] text-mist-400">
                      Copy this key now. For security reasons, it will not be shown again.
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={createdKey.key}
                        onFocus={(e) => e.target.select()}
                        className="input flex-1 min-w-[200px] font-mono text-xs"
                      />
                      <button
                        type="button"
                        onClick={handleCopyKey}
                        className="btn-accent-outline whitespace-nowrap gap-1.5"
                      >
                        <Icon name={keyCopied ? 'check' : 'copy'} size={14} />
                        {keyCopied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCreatedKey(null)}
                    className="flex-none text-mist-500 hover:text-mist-200 bg-transparent border-0 cursor-pointer text-lg leading-none"
                    aria-label="Dismiss"
                  >
                    &times;
                  </button>
                </div>
              </div>
            )}

            {/* Create key form */}
            {showNewKey && (
              <form onSubmit={handleCreateKey} className="flex flex-wrap items-center gap-2 mb-4">
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="Key name (e.g. Production)"
                  className="input flex-1 min-w-[200px]"
                  required
                  maxLength={100}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={creatingKey || !newKeyName.trim()}
                  className="btn-grad whitespace-nowrap"
                >
                  {creatingKey ? 'Creating...' : 'Create key'}
                </button>
              </form>
            )}

            {/* Keys list */}
            {keysLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size={26} />
              </div>
            ) : apiKeys.length === 0 ? (
              <p className="text-[13px] text-mist-500 pt-[13px] border-t border-ink-800 m-0">
                No API keys created yet.
              </p>
            ) : (
              apiKeys.map((key) => (
                <div
                  key={key.id}
                  className="flex flex-wrap items-center gap-x-[14px] gap-y-2 py-[13px] border-t border-ink-800"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p
                        className={`m-0 text-[13.5px] font-semibold truncate ${
                          key.is_active ? 'text-mist-200' : 'text-mist-500 line-through'
                        }`}
                      >
                        {key.name}
                      </p>
                      {!key.is_active && <span className="badge badge-danger">Revoked</span>}
                    </div>
                    <p className="mt-[3px] mb-0 text-mist-400 text-[12.5px] font-mono truncate">
                      {key.key_prefix}··················
                    </p>
                  </div>
                  <span className="text-mist-500 text-xs whitespace-nowrap">
                    {formatDate(key.created_at)}
                    {' · '}
                    {key.last_used_at ? `Last used ${formatDate(key.last_used_at)}` : 'Never used'}
                  </span>
                  {!!key.is_active &&
                    (revokeConfirm === key.id ? (
                      <span className="flex items-center gap-2">
                        <button
                          onClick={() => handleRevokeKey(key.id)}
                          disabled={revoking}
                          className="text-[12.5px] font-semibold text-[#F0928C] hover:opacity-80 bg-transparent border-0 cursor-pointer disabled:opacity-50"
                        >
                          {revoking ? 'Revoking...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setRevokeConfirm(null)}
                          disabled={revoking}
                          className="text-[12.5px] font-semibold text-mist-500 hover:text-mist-300 bg-transparent border-0 cursor-pointer disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setRevokeConfirm(key.id)}
                        className="flex-none text-mist-500 hover:text-[#F0928C] bg-transparent border-0 cursor-pointer transition-colors"
                        aria-label={`Revoke ${key.name}`}
                      >
                        <Icon name="trash" size={16} />
                      </button>
                    ))}
                </div>
              ))
            )}

            <p className="mt-4 mb-0 text-[12.5px] text-mist-500">
              Learn how to integrate Audiaro into your applications in the{' '}
              <a
                href="https://docs.audiaro.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold underline"
                style={{ color: 'var(--a-hi)' }}
              >
                API documentation
              </a>
              .
            </p>
          </div>
        )}
      </div>

      {/* Security */}
      <div className="card">
        <div className="mb-[18px]">
          <CardTitle>Security</CardTitle>
        </div>
        <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-[14px]">
          <div>
            <label className="label">Current password</label>
            <input
              type="password"
              value={passwordData.current_password}
              onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
              className="input"
              required
            />
          </div>
          <div>
            <label className="label">New password</label>
            <input
              type="password"
              value={passwordData.new_password}
              onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
              className="input"
              required
              minLength={8}
            />
            <p className="mt-[7px] text-xs text-mist-500">
              Must be at least 8 characters with uppercase, lowercase, and number.
            </p>
          </div>
          <div>
            <label className="label">Confirm new password</label>
            <input
              type="password"
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
              className="input"
              required
            />
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={saving} className="btn-ghost">
              {saving ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
